import { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { Layout } from './components/Layout';
import { RouteErrorElement } from './components/ErrorBoundary';
import { PwaAutoUpdate } from './components/offline/PwaAutoUpdate';
import AuthCallbackPage from './pages/AuthCallbackPage';

const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const MasterAdminPage = lazy(() => import('./pages/MasterAdminPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const SchoolSettingsPage = lazy(() => import('./pages/SchoolSettingsPage'));
const LocationsPage = lazy(() => import('./pages/LocationsPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const LocationCalendarPage = lazy(() => import('./pages/LocationCalendarPage'));
const SchedulesPage = lazy(() => import('./pages/SchedulesPage'));
const SessionsPage = lazy(() => import('./pages/SessionsPage'));
const StudentsPage = lazy(() => import('./pages/StudentsPage'));
const CoachesPage = lazy(() => import('./pages/CoachesPage'));
const PriceListsPage = lazy(() => import('./pages/PriceListsPage'));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const MailingPage = lazy(() => import('./pages/MailingPage'));
const RegulationsPage = lazy(() => import('./pages/RegulationsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const CreateSchoolPage = lazy(() => import('./pages/CreateSchoolPage'));
const StudentPortalPage = lazy(() => import('./pages/StudentPortalPage'));
const StudentEnrollmentsPage = lazy(() => import('./pages/StudentEnrollmentsPage'));
const StudentCampsPage = lazy(() => import('./pages/StudentCampsPage'));
const CoachPortalPage = lazy(() => import('./pages/CoachPortalPage'));
const CoachDetailPage = lazy(() => import('./pages/CoachDetailPage'));
const StudentDetailPage = lazy(() => import('./pages/StudentDetailPage'));
const ClassesPage = lazy(() => import('./pages/ClassesPage'));
const JoinSchoolPage = lazy(() => import('./pages/JoinSchoolPage'));
const SelectSchoolPage = lazy(() => import('./pages/SelectSchoolPage'));
const ScheduleDetailPage = lazy(() => import('./pages/ScheduleDetailPage'));
const SessionDetailPage = lazy(() => import('./pages/SessionDetailPage'));
const CoachReportsPage = lazy(() => import('./pages/CoachReportsPage'));
const CoachPastSessionsPage = lazy(() => import('./pages/CoachPastSessionsPage'));
const PaymentResultPage = lazy(() => import('./pages/PaymentResultPage'));
const RedeemCouponPage = lazy(() => import('./pages/RedeemCouponPage'));
const DiscountCouponsPage = lazy(() => import('./pages/DiscountCouponsPage'));
const ClaimWaitlistPage = lazy(() => import('./pages/ClaimWaitlistPage'));
const AdminMakeUpCreditsPage = lazy(() => import('./pages/AdminMakeUpCreditsPage'));
const PromosPage = lazy(() => import('./pages/PromosPage'));
const MediaPage = lazy(() => import('./pages/MediaPage'));
const StatisticsPage = lazy(() => import('./pages/StatisticsPage'));
const IndividualClassesPage = lazy(() => import('./pages/IndividualClassesPage'));
const CoachIndividualClassesPage = lazy(() => import('./pages/CoachIndividualClassesPage'));
const IndividualClassesRegulationPage = lazy(() => import('./pages/IndividualClassesRegulationPage'));
const TournamentsPage = lazy(() => import('./pages/TournamentsPage'));
const TournamentDetailPage = lazy(() => import('./pages/TournamentDetailPage'));
const TournamentReportPage = lazy(() => import('./pages/TournamentReportPage'));
const TournamentQrPosterPage = lazy(() => import('./pages/TournamentQrPosterPage'));
const PublicTournamentPage = lazy(() => import('./pages/PublicTournamentPage'));
const CampsPage = lazy(() => import('./pages/CampsPage'));
const RacketStringingPage = lazy(() => import('./pages/RacketStringingPage'));
const CampDetailPage = lazy(() => import('./pages/CampDetailPage'));
const PublicCampTermsPage = lazy(() => import('./pages/PublicCampTermsPage'));
const CampRegistrationLinkPage = lazy(() => import('./pages/CampRegistrationLinkPage'));
const CampRegistrationPage = lazy(() => import('./pages/CampRegistrationPage'));


export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/auth/callback',
    element: <AuthCallbackPage />,
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/schools/new',
    element: <CreateSchoolPage />,
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/join/:schoolId',
    element: <JoinSchoolPage />,
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/select-school',
    element: <SelectSchoolPage />,
    errorElement: <RouteErrorElement />,
  },
  {
    // Public live tournament page — outside Layout, no login required; always switches to the newest version.
    path: '/t/:schoolId/:tournamentId',
    element: <><PwaAutoUpdate /><PublicTournamentPage /></>,
    errorElement: <RouteErrorElement />,
  },
  {
    // Camp registration deep link (school website) — signs in / joins the school, then opens the registration page.
    path: '/s/:schoolId/camps/:slug',
    element: <CampRegistrationLinkPage />,
    errorElement: <RouteErrorElement />,
  },
  {
    // Public camp terms (current or a specific version) — outside Layout, no login required.
    path: '/s/:schoolId/camps/:slug/terms',
    element: <><PwaAutoUpdate /><PublicCampTermsPage /></>,
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/s/:schoolId/camps/:slug/terms/v/:version',
    element: <><PwaAutoUpdate /><PublicCampTermsPage /></>,
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/',
    element: <Layout />,
    errorElement: <RouteErrorElement />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'admin', element: <MasterAdminPage /> },
      { path: 'schools/:schoolId/dashboard', element: <DashboardPage /> },
      { path: 'schools/:schoolId/settings', element: <SchoolSettingsPage /> },
      { path: 'schools/:schoolId/locations', element: <LocationsPage /> },
      { path: 'schools/:schoolId/locations/:locationId/calendar', element: <LocationCalendarPage /> },
      { path: 'schools/:schoolId/calendar', element: <CalendarPage /> },
      { path: 'schools/:schoolId/schedules', element: <SchedulesPage /> },
      { path: 'schools/:schoolId/sessions', element: <SessionsPage /> },
      { path: 'schools/:schoolId/students', element: <StudentsPage /> },
      { path: 'schools/:schoolId/students/:studentMemberId', element: <StudentDetailPage /> },
      { path: 'schools/:schoolId/coaches', element: <CoachesPage /> },
      { path: 'schools/:schoolId/coaches/:coachMemberId', element: <CoachDetailPage /> },
      { path: 'schools/:schoolId/pricelists', element: <PriceListsPage /> },
      { path: 'schools/:schoolId/payments', element: <PaymentsPage /> },
      { path: 'schools/:schoolId/notifications', element: <NotificationsPage /> },
      { path: 'schools/:schoolId/mailing', element: <MailingPage /> },
      { path: 'schools/:schoolId/support/regulations', element: <RegulationsPage /> },
      { path: 'schools/:schoolId/classes', element: <ClassesPage /> },
      { path: 'schools/:schoolId/classes/:viewMode', element: <ClassesPage /> },
      { path: 'schools/:schoolId/schedules/:scheduleId', element: <ScheduleDetailPage /> },
      { path: 'schools/:schoolId/sessions/:sessionId', element: <SessionDetailPage /> },
      { path: 'schools/:schoolId/student', element: <StudentPortalPage /> },
      { path: 'schools/:schoolId/student/enrollments', element: <StudentEnrollmentsPage /> },
      { path: 'schools/:schoolId/student/camps', element: <StudentCampsPage /> },
      { path: 'schools/:schoolId/coach', element: <CoachPortalPage /> },
      { path: 'schools/:schoolId/coach/past-sessions', element: <CoachPastSessionsPage /> },
      { path: 'schools/:schoolId/individual-classes', element: <IndividualClassesPage /> },
      { path: 'schools/:schoolId/coach/individual-classes', element: <CoachIndividualClassesPage /> },
      { path: 'schools/:schoolId/regulations/individual-classes', element: <IndividualClassesRegulationPage /> },
      { path: 'schools/:schoolId/coach-reports', element: <CoachReportsPage /> },
      { path: 'schools/:schoolId/payments/result', element: <PaymentResultPage /> },
      { path: 'schools/:schoolId/redeem-coupon', element: <RedeemCouponPage /> },
      { path: 'schools/:schoolId/coupons', element: <DiscountCouponsPage /> },
      { path: 'schools/:schoolId/makeup-credits', element: <AdminMakeUpCreditsPage /> },
      { path: 'schools/:schoolId/promos', element: <PromosPage /> },
      { path: 'schools/:schoolId/events', element: <PromosPage /> },
      { path: 'schools/:schoolId/media', element: <MediaPage /> },
      { path: 'schools/:schoolId/statistics', element: <StatisticsPage /> },
      { path: 'schools/:schoolId/tournaments', element: <TournamentsPage /> },
      { path: 'schools/:schoolId/tournaments/:tournamentId', element: <TournamentDetailPage /> },
      { path: 'schools/:schoolId/tournaments/:tournamentId/report', element: <TournamentReportPage /> },
      { path: 'schools/:schoolId/tournaments/:tournamentId/qr-poster', element: <TournamentQrPosterPage /> },
      { path: 'schools/:schoolId/camps', element: <CampsPage /> },
      { path: 'schools/:schoolId/racket-stringing', element: <RacketStringingPage /> },
      { path: 'schools/:schoolId/camps/register/:slug', element: <CampRegistrationPage /> },
      { path: 'schools/:schoolId/camps/:campId', element: <CampDetailPage /> },
      { path: 'schools/:schoolId/waitlist/:id/claim', element: <ClaimWaitlistPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
